import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import api from '../utils/Api';

const SendAdminSmsModal = ({ open, onClose, school }) => {
  const [message, setMessage] = useState('');
  const [senderId, setSenderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError('Please enter a valid message.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await api.post(`/super-admin/schools/${school._id}/send-sms`, {
        message: message.trim(),
        senderId: senderId.trim() || undefined
      });
      
      setSuccess(res.data.message || `Successfully sent SMS to admins of ${school.name}`);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to send SMS message');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setSenderId('');
    setError('');
    setSuccess('');
    onClose();
  };

  const charCount = message.length;
  const partsCount = Math.ceil(charCount / 160) || 1;

  return (
    <Dialog open={open} onClose={!loading ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Send SMS to School Admin</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a direct system SMS message to the administrators of <strong>{school?.name}</strong>.
            This will be sent using the system's master Arkesel channel and will not charge the school's allocated SMS credits.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <TextField
            fullWidth
            label="Sender ID (max 11 characters)"
            value={senderId}
            onChange={(e) => setSenderId(e.target.value.substring(0, 11))}
            disabled={loading || !!success}
            placeholder="SYSTEM"
            sx={{ mb: 2 }}
            helperText="Custom alphanumeric name that displays as the sender (max 11 characters)."
          />

          <TextField
            fullWidth
            label="Message Body"
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={loading || !!success}
            placeholder="Type your message here..."
            required
            autoFocus
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, px: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Characters: {charCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              SMS Parts: {partsCount} (160 chars/part)
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading || !!success} variant="outlined">Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading || !!success || !message.trim()}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Message'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SendAdminSmsModal;
